/* ============================================================
   Static lookups + live registries for the YouTube Auto-Dub
   feature. This file used to hold mock data; it is now the seam
   the real backend feeds through (see useAutoDubData.jsx).
   The exported `let` bindings are reassigned by the setters —
   ES-module live bindings keep every importer up to date.
   ============================================================ */

// providers AudioDub can centralise — only YouTube is live today
export const PROVIDERS = [
	{ id: 'youtube', name: 'YouTube', live: true },
	{ id: 'tiktok', name: 'TikTok', live: false },
	{ id: 'instagram', name: 'Instagram', live: false },
];

// ---- languages ----
// The dub pipeline identifies languages by their full name (same values the
// Create-a-Dub flow submits as translate_language). Languages are shown as
// plain names — deliberately NO country flags: a language is not a country
// (Mandarin, Cantonese, English, etc. are spoken across many nations) and flag
// emojis raise avoidable political sensitivities (e.g. CN/TW/HK).

// Seeded from repositories.project.languages() by StepLanguages (the same
// source as Setup your project → Translate Language); 'auto' is excluded.
export let LANGUAGES = [
	{ code: 'English', name: 'English' },
	{ code: 'Hindi', name: 'Hindi' },
	{ code: 'Cantonese', name: 'Cantonese' },
	{ code: 'Mandarin', name: 'Mandarin' },
];

export function setLanguages(list) {
	if (list?.length) LANGUAGES = list;
}

// ---- voice language mapping ----
// Voices in the catalog are tagged with locale variants (e.g. "English (Australia)",
// "Chinese (Cantonese)"). A dub target language should match every voice that
// belongs to that language family, not just the one whose label equals the code.
// Each target code maps to the catalog-label "terms" that count as that language;
// resolveVoiceLanguages() expands those against the live catalog (GET
// /projects/language) so open-ended families (English (Canada), (UK)…) are picked
// up automatically. Add a code here when its catalog labels don't start with the code.
export const VOICE_LANGUAGE_MATCHES = {
	English: ['English'],                              // English, English (Australia), English (Canada), …
	Hindi: ['Hindi'],                                  // Hindi, Hindi (India)
	Cantonese: ['Cantonese', 'Chinese (Cantonese)'],   // Cantonese, Chinese (Cantonese)
	Mandarin: ['Mandarin', 'Chinese (Mandarin)'],      // Chinese (Mandarin)
};

// Given a dub target language code and the catalog's distinct voice-language
// labels, return the labels whose voices should appear for that code. A label
// matches a term when it equals the term or begins with "<term> (" (the locale
// variant form). Falls back to the raw terms (or code) when the catalog hasn't
// loaded or nothing matches, so the picker is never accidentally empty.
export function resolveVoiceLanguages(code, catalog = []) {
	const terms = VOICE_LANGUAGE_MATCHES[code] || [code];
	const lc = terms.map(t => t.toLowerCase());
	const matched = (catalog || []).filter((label) => {
		const l = String(label || '').toLowerCase();
		return lc.some(t => l === t || l.startsWith(`${t} (`));
	});
	return matched.length ? matched : terms;
}

export const STAGES = {
	detected:   { label: 'Detected',        tone: 'default',   color: '#9aa2b4' },
	dubbing:    { label: 'Dubbing',         tone: 'primary',   color: '#5A0AE0' },
	review:     { label: 'Awaiting review', tone: 'warning',   color: '#f6a609' },
	publishing: { label: 'Publishing',      tone: 'secondary', color: '#8C2CE2' },
	published:  { label: 'Published',       tone: 'success',   color: '#16c098' },
	failed:     { label: 'Failed',          tone: 'danger',    color: '#f23e5c' },
};

export const STATUS = {
	active: { label: 'Active', color: '#16c098', tone: 'success' },
	paused: { label: 'Paused', color: '#9aa2b4', tone: 'default' },
	review: { label: 'Needs review', color: '#f6a609', tone: 'warning' },
	draft:  { label: 'Draft', color: '#9aa2b4', tone: 'default' },
};

// rate: credits per second of source video per language (seeded from
// GET /auto-dub/config by useAutoDubConfig)
export let CREDIT_RATE = 1;

// current credit balance (seeded from GET /auto-dub/config)
export let CREDIT_BALANCE = 0;

export function setCreditConfig({ rate, balance }) {
	if (rate != null) CREDIT_RATE = rate;
	if (balance != null) CREDIT_BALANCE = balance;
}

// playlists available on the selected source channel (Step 2 filter,
// seeded from GET /channels/{id}/playlists by useSourcePlaylists)
export let PLAYLISTS = [];

export function setPlaylists(names) {
	PLAYLISTS = names ?? [];
}

// ---- channel registry (seeded from GET /channels by useAutoDubChannels) ----
let _registry = new Map();

export function setChannelRegistry(list) {
	_registry = new Map((list ?? []).map(c => [String(c.id), c]));
}

// ---- lookup helpers ----
export const lang = (code) =>
	LANGUAGES.find(l => l.code === code || l.name === code)
	|| { name: code };
export const channel = (id) => _registry.get(String(id));
